import React, { useCallback, useEffect, useState } from 'react';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';

/**
 * Fullscreen photo lightbox.
 * Props:
 *   images   Array<string>  – data URIs or URLs
 *   index    number         – which image is currently open
 *   onClose  () => void
 *   onIndex  (i:number) => void   – (optional) update when user navigates prev/next
 *
 * Behaviour:
 *   • Esc / click backdrop / X button → close
 *   • Arrow keys ← → and on-screen chevrons → navigate (if >1 image)
 *   • Locks page scroll while open
 *   • Renders nothing when index < 0
 */
export const PhotoLightbox = ({ images = [], index = -1, onClose, onIndex }) => {
  const has = index >= 0 && index < images.length;

  const goPrev = useCallback(() => {
    if (!has || images.length < 2) return;
    const next = (index - 1 + images.length) % images.length;
    onIndex?.(next);
  }, [has, images.length, index, onIndex]);

  const goNext = useCallback(() => {
    if (!has || images.length < 2) return;
    const next = (index + 1) % images.length;
    onIndex?.(next);
  }, [has, images.length, index, onIndex]);

  useEffect(() => {
    if (!has) return undefined;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.();
      else if (e.key === 'ArrowLeft') goPrev();
      else if (e.key === 'ArrowRight') goNext();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener('keydown', onKey);
    };
  }, [has, onClose, goPrev, goNext]);

  if (!has) return null;

  return (
    <div
      data-testid="photo-lightbox"
      role="dialog"
      aria-modal="true"
      aria-label="Photo preview"
      className="fixed inset-0 z-[9999] bg-black/90 backdrop-blur-sm flex items-center justify-center p-4 select-none"
      onClick={onClose}
    >
      {/* Close button (top-right) */}
      <button
        type="button"
        data-testid="photo-lightbox-close"
        aria-label="Close photo"
        onClick={(e) => { e.stopPropagation(); onClose?.(); }}
        className="fixed top-4 right-4 w-10 h-10 rounded-full bg-white text-black flex items-center justify-center hover:bg-[#FF5A1F] hover:text-white shadow-lg transition"
      >
        <X size={22} />
      </button>

      {/* Prev / Next arrows (only if >1 image) */}
      {images.length > 1 && (
        <>
          <button
            type="button"
            data-testid="photo-lightbox-prev"
            aria-label="Previous photo"
            onClick={(e) => { e.stopPropagation(); goPrev(); }}
            className="fixed left-4 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/95 text-black flex items-center justify-center hover:bg-[#FF5A1F] hover:text-white shadow-lg transition"
          >
            <ChevronLeft size={24} />
          </button>
          <button
            type="button"
            data-testid="photo-lightbox-next"
            aria-label="Next photo"
            onClick={(e) => { e.stopPropagation(); goNext(); }}
            className="fixed right-4 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/95 text-black flex items-center justify-center hover:bg-[#FF5A1F] hover:text-white shadow-lg transition"
          >
            <ChevronRight size={24} />
          </button>

          {/* Counter */}
          <div className="fixed top-5 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-white/15 text-white text-xs font-mono uppercase tracking-widest">
            {index + 1} / {images.length}
          </div>
        </>
      )}

      {/* Image itself — clicking on it does not close (only the backdrop closes) */}
      <img
        src={images[index]}
        alt={`Photo ${index + 1} of ${images.length}`}
        onClick={(e) => e.stopPropagation()}
        className="max-w-full max-h-full object-contain rounded-md shadow-2xl"
      />
    </div>
  );
};

/**
 * Convenience hook that pairs with PhotoLightbox.
 * Accepts images dynamically at open() time so a single lightbox on a page
 * can service many photo groups (e.g. multiple bags per booking).
 *
 *   const { open, gallery } = usePhotoLightbox();
 *   <img onClick={() => open(bag.images, imgIdx)} ... />
 *   {gallery}
 */
export const usePhotoLightbox = () => {
  const [images, setImages] = useState([]);
  const [index, setIndex] = useState(-1);
  const open = useCallback((imgs, startIdx = 0) => {
    if (!imgs || imgs.length === 0) return;
    setImages(imgs);
    setIndex(Math.max(0, Math.min(startIdx, imgs.length - 1)));
  }, []);
  const close = useCallback(() => setIndex(-1), []);
  const gallery = (
    <PhotoLightbox images={images} index={index} onIndex={setIndex} onClose={close} />
  );
  return { open, close, gallery };
};

export default PhotoLightbox;
